import { useAppQuery } from '@/helpers/api/query'

import { createInstance } from '@/helpers/api/base'
import { useGarageStore } from '@/modules/FindGarage'
import { UpdatedLog } from '@/types/logs'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { useAreaStore, useGetAreas } from '@/modules/Areas'
import { useMemo } from 'react'

export const GET_UPDATED_LOGS_KEY = 'updatedLogs'

const ENABLED_FOR = [Role.MileageClerk, Role.PerformanceManager]

export const useGetUpdatedLogs = () => {
    const { garage } = useGarageStore()
    const { data: areas = [] } = useGetAreas()
    const { selectedAreaCode } = useAreaStore()
    const { role } = useAuthStore()

    const routesToLoad = useMemo(() => {
        if (role === Role.MileageClerk) {
            return []
        }

        const selectedArea = areas?.find((item) => item.code === selectedAreaCode)
        const source = selectedArea ? selectedArea.workstations : areas.flatMap((item) => item.workstations)

        return source.flatMap((item) => item.routes.flatMap((route) => route.route))
    }, [areas, selectedAreaCode, role])

    const enabledByRole = role === Role.PerformanceManager ? !!routesToLoad?.length : !!garage

    const queryKey = useMemo(
        () =>
            garage?.shortCode
                ? [GET_UPDATED_LOGS_KEY, routesToLoad, garage.shortCode]
                : [GET_UPDATED_LOGS_KEY, routesToLoad],
        [routesToLoad, garage?.shortCode]
    )

    return useAppQuery<UpdatedLog[]>({
        queryKey,
        queryFn: ({ queryKey, signal }) =>
            createInstance({
                url: '/controller-logs/updated',
                method: 'GET',
                params: {
                    Routes: queryKey[1] as string[],
                    GarageSc: queryKey[2] as string,
                },
                paramsSerializer: {
                    indexes: null,
                },
                signal,
            }),
        select: (data) => {
            return [...data].reverse()
        },
        enabled: !!role && ENABLED_FOR.includes(role) && enabledByRole,
        refetchInterval: 1000 * 60,
        refetchIntervalInBackground: true,
    })
}
